export type AuthLoginIssue = "firewall" | "slow" | "okta" | "unknown";

export type AuthLoginError = {
  issue: AuthLoginIssue;
  title: string;
  message: string;
};

export type AuthLoginLocationState = {
  authLoginIssue?: AuthLoginIssue;
};

type ErrorLike = {
  name?: string;
  message?: string;
  errorSummary?: string;
  statusCode?: number;
  xhr?: {
    status?: number;
    responseText?: string;
    responseJSON?: {
      errorSummary?: string;
    };
  };
};

const AUTH_LOGIN_ERROR_COPY: Record<AuthLoginIssue, Omit<AuthLoginError, "issue">> = {
  firewall: {
    title: "Unable to reach Cargill Identity",
    message:
      "This usually points to a firewall, proxy, VPN, or blocked network path. Check your connection settings and try again.",
  },
  slow: {
    title: "Sign-in is taking too long",
    message: "Your connection may be slow or timing out. Wait a moment and try again, or switch networks if possible.",
  },
  okta: {
    title: "Cargill Identity is currently unavailable",
    message:
      "This appears to be an Okta-side service issue. Please try again in a few minutes or contact support if it continues.",
  },
  unknown: {
    title: "We could not complete sign-in",
    message:
      "Please try again. If the problem continues, contact support and mention that the login flow failed in the browser.",
  },
};

const TIMEOUT_STATUSES = new Set([408, 504, 522, 524, 598, 599]);

function isAuthLoginIssue(value: unknown): value is AuthLoginIssue {
  return value === "firewall" || value === "slow" || value === "okta" || value === "unknown";
}

function extractErrorLike(error: unknown): ErrorLike {
  if (!error || typeof error !== "object") {
    return {};
  }

  return error as ErrorLike;
}

function getCombinedErrorText(error: ErrorLike) {
  return [error.name, error.message, error.errorSummary, error.xhr?.responseText, error.xhr?.responseJSON?.errorSummary]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function classifyAuthLoginIssue(error: unknown): AuthLoginIssue | null {
  const authError = extractErrorLike(error);
  const status = authError.statusCode ?? authError.xhr?.status ?? null;
  const text = getCombinedErrorText(authError);
  const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;

  const looksLikeTimeout = /(timed out|timeout|took too long|deadline exceeded|request aborted|gateway timeout)/.test(
    text,
  );
  const looksLikeNetworkFailure =
    /(network request failed|failed to fetch|load failed|network error|networkerror|dns|proxy|firewall|vpn|cors|err_network|unable to connect|access gateway timeout)/.test(
      text,
    );
  const looksLikeOktaIssue =
    /(service unavailable|temporarily unavailable|internal server error|server error|too many requests|rate limit|try again later|okta)/.test(
      text,
    );

  if (isOffline) {
    return "firewall";
  }

  if (TIMEOUT_STATUSES.has(status ?? -1) || looksLikeTimeout) {
    return "slow";
  }

  if (status === 0 || looksLikeNetworkFailure) {
    return "firewall";
  }

  if (status === 429 || (status !== null && status >= 500) || looksLikeOktaIssue) {
    return "okta";
  }

  return null;
}

export function getAuthLoginError(issue: AuthLoginIssue): AuthLoginError {
  return {
    issue,
    ...AUTH_LOGIN_ERROR_COPY[issue],
  };
}

export function getKnownAuthLoginError(error: unknown): AuthLoginError | null {
  const issue = classifyAuthLoginIssue(error);
  return issue ? getAuthLoginError(issue) : null;
}

export function getAuthLoginErrorOrFallback(error: unknown): AuthLoginError {
  const issue = classifyAuthLoginIssue(error) ?? "unknown";
  return getAuthLoginError(issue);
}

export function getAuthLoginErrorFromLocationState(state: unknown): AuthLoginError | null {
  if (!state || typeof state !== "object") {
    return null;
  }

  const authLoginIssue = (state as AuthLoginLocationState).authLoginIssue;
  return isAuthLoginIssue(authLoginIssue) ? getAuthLoginError(authLoginIssue) : null;
}
